import { supabase } from './supabase'
import { videoCache, commentCache, userCache, genreCache, searchCache } from './cache'

// ── Batch Requests Helper ──────────────────────────────────────────────────
// Instead of multiple separate queries, batch them into one roundtrip
export const batchRequests = async <T extends Record<string, Promise<any>>>(
  requests: T
): Promise<{ [K in keyof T]: Awaited<T[K]> }> => {
  const keys = Object.keys(requests)
  const promises = Object.values(requests)
  const results = await Promise.all(promises)
  
  return keys.reduce((acc, key, index) => {
    acc[key as keyof T] = results[index]
    return acc
  }, {} as any)
}

// ── Optimized Video Fetch with Counts ──────────────────────────────────────
export const getVideosWithCounts = async (
  filters: {
    contentType?: string | null
    genreId?: string | null
    sortBy?: string
    isPublic?: boolean
    limit?: number
    offset?: number
  } = {}
) => {
  const cacheKey = `videos_${JSON.stringify(filters)}`
  const cached = videoCache.get<any[]>(cacheKey)
  if (cached) {
    console.log('📦 Using cached videos:', cacheKey)
    return cached
  }

  console.log('🔄 Fetching fresh videos:', cacheKey)
  
  let query = supabase
    .from('videos')
    .select('*, genre:genres(name_en), user:profiles(username)')
    
  if (filters.isPublic !== undefined) {
    query = query.eq('is_public', filters.isPublic)
  }
  
  if (filters.contentType) {
    query = query.eq('content_type', filters.contentType)
  }
  
  if (filters.genreId) {
    query = query.eq('genre_id', filters.genreId)
  }
  
  if (filters.sortBy === 'popular') {
    query = query.order('likes_count', { ascending: false })
  } else if (filters.sortBy === 'trending' || filters.sortBy === 'most_viewed') {
    query = query.order('views', { ascending: false })
  } else {
    query = query.order('created_at', { ascending: false })
  }
  
  if (filters.limit) {
    query = query.limit(filters.limit)
  }
  
  if (filters.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1)
  }

  const { data, error } = await query
  
  if (error || !data) return []

  // Get all video IDs for batch count fetching
  const videoIds = data.map(v => v.id)
  
  // Batch fetch likes and comments counts using a single query each
  const [likesData, commentsData] = await Promise.all([
    supabase
      .from('likes')
      .select('video_id')
      .in('video_id', videoIds),
    supabase
      .from('comments')
      .select('video_id')
      .in('video_id', videoIds)
  ])

  // Create count maps
  const likesMap = new Map<string, number>()
  const commentsMap = new Map<string, number>()
  
  likesData.data?.forEach(l => {
    likesMap.set(l.video_id, (likesMap.get(l.video_id) || 0) + 1)
  })
  
  commentsData.data?.forEach(c => {
    commentsMap.set(c.video_id, (commentsMap.get(c.video_id) || 0) + 1)
  })

  const enriched = data.map(v => ({
    ...v,
    views: v.views || 0,
    likes_count: likesMap.get(v.id) || 0,
    comments_count: commentsMap.get(v.id) || 0
  }))

  // Cache the result
  videoCache.set(cacheKey, enriched, 300) // 5 minutes TTL
  return enriched
}

// ── Optimized Comments Fetch ───────────────────────────────────────────────
export const getCommentsWithReplies = async (videoId: string) => {
  const cacheKey = `comments_${videoId}`
  const cached = commentCache.get<any[]>(cacheKey)
  if (cached) {
    console.log('📦 Using cached comments:', cacheKey)
    return cached
  }

  console.log('🔄 Fetching fresh comments:', cacheKey)

  const { data, error } = await supabase
    .from('comments')
    .select('*, user:profiles(username)')
    .eq('video_id', videoId)
    .order('created_at', { ascending: true })

  if (error || !data) return { roots: [], count: 0 }

  // Build tree structure
  const map = new Map<string, any>()
  data.forEach(c => map.set(c.id, { ...c, replies: [] }))
  const roots: any[] = []
  
  data.forEach(c => {
    if (c.parent_id && map.has(c.parent_id)) {
      map.get(c.parent_id)!.replies!.push(map.get(c.id)!)
    } else {
      roots.push(map.get(c.id)!)
    }
  })

  const result = { roots, count: data.length }
  commentCache.set(cacheKey, result, 120) // 2 minutes TTL
  return result
}

// ── Optimized User Profile Fetch ───────────────────────────────────────────
export const getUserProfile = async (userId: string) => {
  const cacheKey = `user_${userId}`
  const cached = userCache.get<any>(cacheKey)
  if (cached) {
    console.log('📦 Using cached user:', cacheKey)
    return cached
  }

  const { data } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  if (data) {
    userCache.set(cacheKey, data, 600) // 10 minutes TTL
  }

  return data
}

// ── Optimized Genres Fetch ─────────────────────────────────────────────────
export const getGenres = async () => {
  const cacheKey = 'genres_all'
  const cached = genreCache.get<any[]>(cacheKey)
  if (cached) {
    console.log('📦 Using cached genres')
    return cached
  }

  const { data } = await supabase
    .from('genres')
    .select('*')
    .order('name_en')

  if (data) {
    genreCache.set(cacheKey, data, 1800) // 30 minutes TTL
  }

  return data || []
}

// ── Optimized Search ───────────────────────────────────────────────────────
export const searchVideos = async (query: string, filters: any = {}) => {
  const cacheKey = `search_${query}_${JSON.stringify(filters)}`
  const cached = searchCache.get<any[]>(cacheKey)
  if (cached) {
    console.log('📦 Using cached search:', cacheKey)
    return cached
  }

  let dbQuery = supabase
    .from('videos')
    .select('*, genre:genres(name_en), user:profiles(username)')
    .eq('is_public', true)

  if (query) {
    dbQuery = dbQuery.or(`title.ilike.%${query}%,description.ilike.%${query}%`)
  }

  if (filters.contentType) {
    dbQuery = dbQuery.eq('content_type', filters.contentType)
  }

  if (filters.genre) {
    dbQuery = dbQuery.eq('genre_id', filters.genre)
  }

  if (filters.sortBy === 'newest') {
    dbQuery = dbQuery.order('created_at', { ascending: false })
  } else if (filters.sortBy === 'popular' || filters.sortBy === 'rating') {
    dbQuery = dbQuery.order('views', { ascending: false })
  } else {
    dbQuery = dbQuery.order('views', { ascending: false })
  }

  dbQuery = dbQuery.limit(50)

  const { data } = await dbQuery

  if (data) {
    searchCache.set(cacheKey, data, 180) // 3 minutes TTL
  }

  return data || []
}

// ── Debounce Utility ───────────────────────────────────────────────────────
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// ── Throttle Utility ───────────────────────────────────────────────────────
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => { inThrottle = false }, limit)
    }
  }
}